import { sGetRaw } from "./scopedStorage";
// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── Phase Groups ──────────────────────────────────────────────────────────────
//
// A "Phase Group" is a named, colored bucket that maps one or more CSI
// MasterFormat division IDs to a construction-sequence label that owners and
// precon managers recognise (e.g. "MEP" rather than "Div 21 / 22 / 23 / 26").
//
// Phase Groups are purely a *presentation* layer — all underlying data still
// ties to CSI divisions and subcategories.  Groups are stored in localStorage
// under `oe_phase_groups_v1` so they persist across sessions and can be
// customised per user/org without touching the estimate data model.
//
// Default groups cover all 23 divisions defined in projectData.ts.

export const LS_PHASE_GROUPS = "oe_phase_groups_v1";

export interface PhaseGroup {
  id: string;       // stable slug, never changes after creation
  name: string;     // user-visible label
  color: string;    // hex color used in schedule bars, cashflow chart, etc.
  divIds: string[]; // division IDs from projectData.ts (e.g. "div1", "div22")
  order: number;    // sort order in lists
}

// ── Default mapping (covers all 23 divisions in projectData.ts) ──────────────
export const DEFAULT_PHASE_GROUPS: PhaseGroup[] = [
  {
    id: "gen-cond",
    name: "General Conditions",
    color: "#94a3b8",
    divIds: ["div1"],
    order: 0,
  },
  {
    id: "demo-site-prep",
    name: "Demo & Site Prep",
    color: "#f59e0b",
    divIds: ["div2", "div31"],
    order: 1,
  },
  {
    id: "foundations",
    name: "Foundations & Concrete",
    color: "#b45309",
    divIds: ["div3"],
    order: 2,
  },
  {
    id: "structure",
    name: "Structure & Framing",
    color: "#ef4444",
    divIds: ["div4", "div5", "div6"],
    order: 3,
  },
  {
    id: "envelope",
    name: "Exterior Envelope",
    color: "#10b981",
    divIds: ["div7", "div8"],
    order: 4,
  },
  {
    id: "mep",
    name: "MEP",
    color: "#3b82f6",
    divIds: ["div21", "div22", "div23", "div25", "div26", "div27", "div28"],
    order: 5,
  },
  {
    id: "finishes",
    name: "Interiors & Finishes",
    color: "#f97316",
    divIds: ["div9", "div10", "div11", "div12"],
    order: 6,
  },
  {
    id: "specialties",
    name: "Specialties & Equipment",
    color: "#8b5cf6",
    divIds: ["div13", "div14"],
    order: 7,
  },
  {
    id: "site-work",
    name: "Site Work",
    color: "#06b6d4",
    divIds: ["div32", "div33"],
    order: 8,
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Return the PhaseGroup that contains this division ID, or undefined. */
export function getDivPhaseGroup(
  divId: string,
  groups: PhaseGroup[]
): PhaseGroup | undefined {
  return groups.find((g) => g.divIds.includes(divId));
}

/**
 * The phase a subcategory belongs to: its own override when it has a live
 * one, otherwise its division's.
 *
 * The override is deliberately forgiving. Phases are user-editable, so a
 * subcategory can name one that has since been deleted or renamed away; when
 * the id no longer resolves we fall back to the division rather than dropping
 * the subcategory into the ungrouped bucket. That keeps deleting a phase in
 * Settings from stranding work, and removes the need for any migration.
 */
export function getSubPhaseGroup(
  divId: string,
  sub: { phaseId?: string } | undefined,
  groups: PhaseGroup[]
): PhaseGroup | undefined {
  if (sub?.phaseId) {
    const override = groups.find((g) => g.id === sub.phaseId);
    if (override) return override;
  }
  return getDivPhaseGroup(divId, groups);
}

/** Whether this subcategory is pinned to a phase other than its division's. */
export function hasPhaseOverride(
  divId: string,
  sub: { phaseId?: string } | undefined,
  groups: PhaseGroup[]
): boolean {
  if (!sub?.phaseId) return false;
  const override = groups.find((g) => g.id === sub.phaseId);
  if (!override) return false;
  return override.id !== getDivPhaseGroup(divId, groups)?.id;
}

/** Return the PhaseGroup color for a division, falling back to a default. */
export function getDivPhaseColor(
  divId: string,
  groups: PhaseGroup[],
  fallback = "#4fd1c5"
): string {
  return getDivPhaseGroup(divId, groups)?.color ?? fallback;
}

/** Group an array of divisions (with .id) by their PhaseGroup, in order.
 *  Divisions not assigned to any group are collected into an "Other" bucket. */
export function groupDivisionsByPhase<D extends { id: string }>(
  divisions: D[],
  groups: PhaseGroup[]
): Array<{ group: PhaseGroup; divisions: D[] }> {
  const byGroup = new Map<string, D[]>();
  groups.forEach((g) => byGroup.set(g.id, []));
  const ungrouped: D[] = [];

  divisions.forEach((div) => {
    const g = getDivPhaseGroup(div.id, groups);
    if (g) byGroup.get(g.id)!.push(div);
    else ungrouped.push(div);
  });

  const result: Array<{ group: PhaseGroup; divisions: D[] }> = [];
  [...groups]
    .sort((a, b) => a.order - b.order)
    .forEach((g) => {
      const divs = byGroup.get(g.id) ?? [];
      if (divs.length > 0) result.push({ group: g, divisions: divs });
    });

  if (ungrouped.length > 0) {
    result.push({
      group: {
        id: "__other__",
        name: "Other",
        color: "#6b7280",
        divIds: [],
        order: 999,
      },
      divisions: ungrouped,
    });
  }
  return result;
}

/** Load phase groups from localStorage, falling back to defaults. */
export function loadPhaseGroups(): PhaseGroup[] {
  try {
    const raw = sGetRaw(LS_PHASE_GROUPS);
    if (raw) {
      const parsed = JSON.parse(raw) as PhaseGroup[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return DEFAULT_PHASE_GROUPS;
}

/** Ensure every division in DEFAULT_PHASE_GROUPS has a home in the user's
 *  groups (migrates if the user's saved groups are missing new defaults). */
export function migratePhaseGroups(saved: PhaseGroup[]): PhaseGroup[] {
  const allAssigned = new Set(saved.flatMap((g) => g.divIds));
  const missing = DEFAULT_PHASE_GROUPS.flatMap((dg) =>
    dg.divIds.filter((id) => !allAssigned.has(id))
  );
  if (missing.length === 0) return saved;
  // Append missing divIds to the matching default group (or create it)
  const result = saved.map((g) => ({ ...g }));
  missing.forEach((divId) => {
    const srcGroup = DEFAULT_PHASE_GROUPS.find((dg) => dg.divIds.includes(divId));
    if (!srcGroup) return;
    const existing = result.find((g) => g.id === srcGroup.id);
    if (existing) {
      existing.divIds = [...existing.divIds, divId];
    } else {
      result.push({ ...srcGroup, divIds: [divId] });
    }
  });
  return result.sort((a, b) => a.order - b.order);
}
